import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BriefCard, { BriefMove } from '../components/BriefCard';
import LedgerCounter from '../components/LedgerCounter';
import LoadingScreen from '../components/LoadingScreen';
import InvoiceInbox from '../components/InvoiceInbox';
import { pageCache } from '../utils/pageCache';
import { fmt } from '../utils/formatters';
import { greetingFor } from '../utils/greeting';
import { isCurrentBrief } from '../utils/briefFreshness';

interface DailyScore {
  score: number;
  band: string;
  headline: string;
  ratio: number | null;
}

interface Brief {
  brief_id: string;
  brief_date?: string;
  type?: string;
  subject?: string;
  created_at?: string;
  send_status?: string;
  moves?: BriefMove[];
  score?: DailyScore;
}

const SCORE_BAND_COLOR: Record<string, string> = {
  banner: '#2E6B4F', strong: '#2E6B4F', steady: '#6B6670',
  soft: '#B54A4A', rough: '#B54A4A', building: '#6B6670',
};

function ScoreCard({ score }: { score: DailyScore }) {
  const color = SCORE_BAND_COLOR[score.band] || '#6B6670';
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card flex items-center gap-5">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 font-serif text-2xl font-bold"
        style={{ border: `2px solid ${color}`, color, background: '#FAF9F7' }}
      >
        {score.score}
      </div>
      <div>
        <div className="text-[11px] font-sans font-medium text-[#6B6670] uppercase tracking-wide">
          Today's Score
        </div>
        <p className="font-serif text-base text-[#2D2A2E] mt-0.5">{score.headline}</p>
      </div>
    </div>
  );
}

interface HistoryBrief {
  brief_id?: string;
  brief_date?: string;
  type?: string;
  subject?: string;
  send_status?: string;
  rec_ids?: string[];
}

interface Ledger {
  lifetime_found_dollars?: number;
  streak_days?: number;
  recent?: any[];
}

/** Defensive date formatter — brief_date may be YYYY-MM-DD, ISO, or missing. */
function formatBriefDate(d?: string) {
  if (!d) return '—';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function BriefPage() {
  const [brief, setBrief] = useState<Brief | null>(() => pageCache.get('/api/brief/today')?.brief ?? null);
  const [briefLoading, setBriefLoading] = useState(() => !pageCache.has('/api/brief/today'));
  const [weeklyBrief, setWeeklyBrief] = useState<Brief | null>(() => pageCache.get('/api/brief/weekly')?.brief ?? null);
  const [ledger, setLedger] = useState<Ledger | null>(() => pageCache.get('/api/ledger') ?? null);
  const [ledgerLoading, setLedgerLoading] = useState(() => !pageCache.has('/api/ledger'));
  const [history, setHistory] = useState<HistoryBrief[]>(() => pageCache.get('/api/brief/history?limit=14')?.briefs ?? []);
  const [historyLoading, setHistoryLoading] = useState(() => !pageCache.has('/api/brief/history?limit=14'));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [tmReport, setTmReport] = useState<any>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/brief/today')
      .then((res) => { pageCache.set('/api/brief/today', res.data); setBrief(res.data?.brief ?? null); })
      .catch(() => { if (!pageCache.has('/api/brief/today')) setBrief(null); })
      .finally(() => setBriefLoading(false));

    axios.get('/api/ledger')
      .then((res) => { pageCache.set('/api/ledger', res.data); setLedger(res.data || null); })
      .catch(() => { if (!pageCache.has('/api/ledger')) setLedger(null); })
      .finally(() => setLedgerLoading(false));

    axios.get('/api/brief/weekly')
      .then((res) => { pageCache.set('/api/brief/weekly', res.data); setWeeklyBrief(res.data?.brief ?? null); })
      .catch(() => { if (!pageCache.has('/api/brief/weekly')) setWeeklyBrief(null); });

    axios.get('/api/brief/history?limit=14')
      .then((res) => { pageCache.set('/api/brief/history?limit=14', res.data); setHistory(Array.isArray(res.data?.briefs) ? res.data.briefs : []); })
      .catch(() => { if (!pageCache.has('/api/brief/history?limit=14')) setHistory([]); })
      .finally(() => setHistoryLoading(false));

    axios.get('/api/timemachine')
      .then((res) => { pageCache.set('/api/timemachine', res.data); setTmReport(res.data?.report ?? null); })
      .catch(() => setTmReport(null));
  }, []);

  const patchMoveStatus = (recId: string, status: string) => {
    const patch = (prev: Brief | null) =>
      prev && prev.moves?.some((m) => m.rec_id === recId)
        ? { ...prev, moves: prev.moves.map((m) => (m.rec_id === recId ? { ...m, status } : m)) }
        : prev;
    setBrief(patch);
    setWeeklyBrief(patch);
  };

  const handleRespond = async (recId: string, action: 'adopt' | 'dismiss') => {
    const prevStatus =
      brief?.moves?.find((m) => m.rec_id === recId)?.status ||
      weeklyBrief?.moves?.find((m) => m.rec_id === recId)?.status ||
      'sent';
    const optimisticStatus = action === 'adopt' ? 'adopted' : 'dismissed';
    setRespondingId(recId);
    patchMoveStatus(recId, optimisticStatus); // optimistic — revert on failure
    try {
      const res = await axios.post('/api/brief/feedback', { rec_id: recId, action });
      const serverStatus = res.data?.rec_status;
      if (serverStatus && serverStatus !== optimisticStatus) {
        patchMoveStatus(recId, serverStatus);
      }
      toast.success(
        action === 'adopt'
          ? "Got it — we'll track how it performs"
          : "Noted — we won't suggest that again"
      );
    } catch {
      patchMoveStatus(recId, prevStatus);
      toast.error("Couldn't save your response. Please try again.");
    } finally {
      setRespondingId(null);
    }
  };

  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const moves = brief?.moves || [];
  // An old brief is history, not today (the Sept 1 score showed on Sept 24).
  const current = isCurrentBrief(brief?.brief_date);
  const todayLine = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">
          {greetingFor(new Date().getHours())}{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="font-sans text-sm text-[#6B6670] mt-0.5">{todayLine}</p>
      </div>

      {/* v4 invoice inbox — the inflow lives on the home page */}
      <InvoiceInbox variant="home" />

      {/* R3 — the daily scoreboard: a glanceable score, no chore required */}
      {brief?.score && current && (
        <div className="mb-8">
          <ScoreCard score={brief.score} />
        </div>
      )}

      {/* The moves — no label: this page is today. */}
      <div className="mb-8">

        {briefLoading ? (
          <LoadingScreen fullScreen={false} />
        ) : brief && current && moves.length > 0 ? (
          <>
            {brief.subject && (
              <h2 className="font-serif text-xl text-[#2D2A2E] mb-4">{brief.subject}</h2>
            )}
            <div className="space-y-4">
              {moves.map((move, i) => (
                <BriefCard
                  key={move.rec_id}
                  move={move}
                  index={i}
                  onRespond={handleRespond}
                  responding={respondingId === move.rec_id}
                />
              ))}
            </div>
          </>
        ) : brief ? (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
            <h3 className="font-serif text-lg text-[#2D2A2E]">Nothing needs you today.</h3>
          </div>
        ) : tmReport && tmReport.total_found_point > 0 ? (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-sans font-medium uppercase tracking-widest text-[#4A2D5E] mb-2">
              <Sparkles size={13} strokeWidth={1.5} /> The Time Machine
            </div>
            <h3 className="font-serif text-xl text-[#2D2A2E] mb-1">
              Had ClearPath been watching, it would have found you{' '}
              <span className="font-sans font-semibold text-[#2E6B4F] tabular-nums">{fmt(tmReport.total_found_point)}</span>
            </h3>
            <p className="font-sans text-sm text-[#6B6670] mb-4">
              {fmt(tmReport.total_found_low)}–{fmt(tmReport.total_found_high)} across {tmReport.weeks_analyzed} weeks,
              from your own history — estimated. Your first live brief lands tomorrow at your send time.
            </p>
            <div className="space-y-2">
              {(tmReport.moves || []).slice(0, 3).map((m: any) => (
                <div key={m.dedupe_key + m.week_of} className="flex items-center justify-between bg-[#FAF9F7] rounded-[10px] px-4 py-2.5">
                  <span className="font-sans text-sm text-[#2D2A2E]">{m.title}</span>
                  <span className="font-sans text-sm font-semibold tabular-nums text-[#2E6B4F]">{fmt(m.impact_point)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card flex flex-col items-start">
            <Sparkles size={28} strokeWidth={1.5} className="text-[#4A2D5E]/40 mb-3" />
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-1">
              Your first brief arrives tomorrow morning
            </h3>
            <p className="font-sans text-sm text-[#6B6670] mb-4 max-w-md">
              Briefs are built from your sales data — make sure your data is connected or
              uploaded so we have something to work with.
            </p>
            <button
              onClick={() => navigate('/settings/data')}
              className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium text-sm px-5 py-2.5 transition-colors"
            >
              Connect your data
            </button>
          </div>
        )}
      </div>

      {/* R3 — the weekly gold action list: the 2-3 moves worth doing this
          week, batched (margin-aware v4 detectors land only here). */}
      {weeklyBrief && (weeklyBrief.moves || []).length > 0 && (
        <div className="mb-8">
          <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-3">
            This Week's Gold Moves
          </div>
          <div className="space-y-4">
            {(weeklyBrief.moves || []).map((move, i) => (
              <BriefCard
                key={move.rec_id}
                move={move}
                index={i}
                onRespond={handleRespond}
                responding={respondingId === move.rec_id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="mb-8">
        {ledgerLoading ? (
          <div className="animate-pulse bg-[#F2F0ED] rounded-[14px] h-24" />
        ) : (
          <LedgerCounter
            adoptedCount={(ledger as any)?.adopted_count || 0}
            measuredCount={(ledger as any)?.measured_count || 0}
            measuredNetRaw={(ledger as any)?.measured_net_raw || 0}
            didntPanOut={(ledger as any)?.didnt_pan_out || 0}
            streakDays={ledger?.streak_days || 0}
          />
        )}
      </div>

      {/* Recent briefs */}
      <div className="mb-8">
        {historyLoading ? (
          <div className="animate-pulse bg-[#F2F0ED] rounded-[14px] h-14" />
        ) : history.length > 0 ? (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] shadow-card overflow-hidden">
            <button
              onClick={() => setHistoryOpen(!historyOpen)}
              className="w-full flex items-center justify-between p-5 hover:bg-[#FAF9F7] transition-colors"
            >
              <span className="font-serif text-base text-[#2D2A2E]">Recent briefs</span>
              <span className="flex items-center gap-2">
                <span className="font-sans text-xs text-[#6B6670]">{history.length}</span>
                {historyOpen ? (
                  <ChevronDown size={16} strokeWidth={1.5} className="text-[#6B6670]" />
                ) : (
                  <ChevronRight size={16} strokeWidth={1.5} className="text-[#6B6670]" />
                )}
              </span>
            </button>
            {historyOpen && (
              <div className="border-t border-[#E6E2DE]">
                {history.map((h, i) => (
                  <div
                    key={h.brief_id || i}
                    className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E6E2DE] last:border-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-sans text-xs text-[#6B6670] tabular-nums whitespace-nowrap w-24 flex-shrink-0">
                        {formatBriefDate(h.brief_date)}
                      </span>
                      <span className="font-sans text-sm text-[#2D2A2E] truncate">
                        {h.subject || 'Daily brief'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
